import { Button, Tabs } from '@/components';
import { JobTimeline } from '@/features/runner-monitor/JobTimeline';
import { JobsTable } from '@/features/runner-monitor/JobsTable';
import {
  FilterSelect,
  QueryStates,
  RunnerPage,
  uniqueOptions,
} from '@/features/runner-monitor/RunnerPage';
import { useRunnerJobs, useRunnerMachines, useRunners } from '@/features/runner-monitor/hooks';
import { useSearchParams } from '@solidjs/router';
import { JOB_RESULTS, type JobResult } from '@uchat-team-management/shared';
import { Show, createMemo } from 'solid-js';

const WINDOWS = { '6h': 6, '24h': 24, '7d': 24 * 7 } as const;
type WindowId = keyof typeof WINDOWS;

type Params = {
  window?: string;
  machine?: string;
  runner?: string;
  repo?: string;
  result?: string;
};

export default function RunnersHistory() {
  const [params, setParams] = useSearchParams<Params>();
  const machines = useRunnerMachines();
  const runners = useRunners();
  const windowId = (): WindowId =>
    params.window && params.window in WINDOWS ? (params.window as WindowId) : '24h';
  // The window's end is fixed per selection so the query key stays stable between refetches.
  const span = createMemo(() => {
    const to = Date.now();
    return { from: to - WINDOWS[windowId()] * 3600_000, to };
  });
  const jobs = useRunnerJobs(() => ({
    limit: 200,
    from: new Date(span().from).toISOString(),
    machineId: params.machine || undefined,
    runner: params.runner || undefined,
    repo: params.repo || undefined,
    result: (JOB_RESULTS as readonly string[]).includes(params.result ?? '')
      ? (params.result as JobResult)
      : undefined,
  }));
  const rows = () => jobs.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <RunnerPage
      title="History"
      sub="When each runner worked, and how it went"
      updatedAt={jobs.dataUpdatedAt}
    >
      <div class="flex flex-wrap items-end gap-3">
        <FilterSelect
          label="Machine"
          value={params.machine}
          options={(machines.data?.data ?? []).map((m) => ({ value: m.id, label: m.name }))}
          onChange={(machine) => setParams({ machine })}
        />
        <FilterSelect
          label="Runner"
          value={params.runner}
          options={uniqueOptions((runners.data?.data ?? []).map((r) => r.name))}
          onChange={(runner) => setParams({ runner })}
        />
        <FilterSelect
          label="Repo"
          value={params.repo}
          options={uniqueOptions((runners.data?.data ?? []).map((r) => r.repo))}
          onChange={(repo) => setParams({ repo })}
        />
        <FilterSelect
          label="Result"
          value={params.result}
          options={[
            { value: 'success', label: 'Success' },
            { value: 'failure', label: 'Failed' },
            { value: 'cancelled', label: 'Cancelled' },
            { value: 'running', label: 'Running' },
          ]}
          onChange={(result) => setParams({ result })}
        />
        <Tabs
          class="ml-auto"
          tabs={[
            { id: '6h', label: '6 hours' },
            { id: '24h', label: '24 hours' },
            { id: '7d', label: '7 days' },
          ]}
          active={windowId()}
          onChange={(id) => setParams({ window: id })}
        />
      </div>
      <QueryStates
        pending={jobs.isPending}
        error={jobs.error}
        empty={rows().length === 0}
        emptyTitle="No jobs in this window"
        emptyBody="Jobs show up here once GitHub reports them (workflow_job webhook or the 60s sync)."
      >
        <JobTimeline jobs={rows()} from={span().from} to={Math.max(span().to, Date.now())} />
        <JobsTable jobs={rows()} />
        <Show when={jobs.hasNextPage}>
          <div class="flex justify-center">
            <Button
              variant="secondary"
              loading={jobs.isFetchingNextPage}
              onClick={() => jobs.fetchNextPage()}
            >
              Load older jobs
            </Button>
          </div>
        </Show>
      </QueryStates>
    </RunnerPage>
  );
}
